import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCourseList, deleteCourseById, toggleCourseStatus } from '../../../redux/actions/Actions';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { Delete, Edit } from '@mui/icons-material';
import { Box, TextField, IconButton, Stack, Switch } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const TABS = [
  { key: 'all',      label: 'All' },
  { key: 'active',   label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const tabStyle = (active) => ({
  padding: '7px 22px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 13,
  background: active ? '#2563EB' : '#F1F5F9',
  color: active ? '#fff' : '#475569',
  transition: 'all 0.15s',
});

const CourseList = () => {
  const fetchCourses = useSelector((state) => state.fetchAllCourses.courses);
  const { user } = useSelector((state) => state.auth);
  const userId = user?.role_id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const userData = (userId === 1 || user?.role_name === 'admin');
  const fetchPrograms = useSelector((state) => state.fetchAllPrograms.programs);

  useEffect(() => { dispatch(fetchCourseList()); }, [dispatch]);

  useEffect(() => {
    const coursesWithProgramName = fetchCourses.map(course => ({
      ...course,
      program_name: fetchPrograms.find(p => p.program_id === course.program_id)?.program_name || course.program_name,
    }));
    setData(coursesWithProgramName);
  }, [fetchCourses, fetchPrograms]);

  const columns = useMemo(() => {
    const common = [
      { field: 'id',           headerName: 'Id',           width: 60,  disableColumnMenu: true, sortable: false },
      { field: 'program_name', headerName: 'Program Name', width: 160, disableColumnMenu: true },
      { field: 'course_name',  headerName: 'Course Name',  width: 160, disableColumnMenu: true },
      { field: 'description',  headerName: 'Description',  width: 160, disableColumnMenu: true },
      { field: 'duration',     headerName: 'Duration',     width: 120, disableColumnMenu: true },
      {
        field: 'is_active',
        headerName: 'Status',
        width: 120,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={!!params.row.is_active}
              onChange={() => dispatch(toggleCourseStatus(params.row.course_id))}
              color="success"
              size="small"
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: params.row.is_active ? '#16A34A' : '#DC2626' }}>
              {params.row.is_active ? 'Active' : 'Inactive'}
            </span>
          </Stack>
        ),
      },
    ];

    const adminCols = userData ? [{
      field: 'action',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => navigate(`/auth/updateCourse/${params.row.course_id}`)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => {
            dispatch(deleteCourseById(params.row.course_id));
            setData(prev => prev.filter(r => r.course_id !== params.row.course_id));
          }}>
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    }] : [];

    return [...common, ...adminCols];
  }, [userId, data, dispatch, navigate]);

  const rows = useMemo(() =>
    data.map((row, index) => ({ ...row, id: index + 1 })),
    [data]
  );

  const tabFiltered = rows.filter(row => {
    if (activeTab === 'active')   return !!row.is_active;
    if (activeTab === 'inactive') return !row.is_active;
    return true;
  });

  const filteredRows = tabFiltered.filter(row =>
    Object.values(row).some(v =>
      v !== null && v !== undefined && v.toString().toLowerCase().includes(searchValue.toLowerCase())
    )
  );

  const counts = {
    all:      rows.length,
    active:   rows.filter(r => !!r.is_active).length,
    inactive: rows.filter(r => !r.is_active).length,
  };

  return (
    <div className="course-list">
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} style={tabStyle(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
            {t.label}
            <span style={{ marginLeft: 6, background: activeTab === t.key ? 'rgba(255,255,255,0.25)' : '#E2E8F0', color: activeTab === t.key ? '#fff' : '#64748B', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
              {counts[t.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <TextField
        label="Search"
        variant="outlined"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        InputProps={{ endAdornment: <SearchIcon /> }}
        style={{ marginBottom: 15 }}
        size="small"
      />

      {(userId === 1 || userId === 3) && (
        <Box sx={{ height: 450, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 5 } } }}
            pageSizeOptions={[5, 10]}
          />
        </Box>
      )}
    </div>
  );
};

export default CourseList;
